// src/components/PromoCodeBox.jsx
//
// Promo-code entry for checkout surfaces (promo-codes slice). The client only
// ever sends the typed CODE STRING to previewPurchase (dry-run CF); the server
// derives the discount -- nothing here computes or trusts an amount. Parent
// pages own the applied state ({ code, preview }) so they can re-price their
// totals and pass the code into the submit call.
//
// Themed like LegalConsent (charcoal = CheckoutPage, burgundy = PayMe page).
import React, { useState, useEffect, useRef } from 'react';
import { AUTH_MODE } from '../auth/firebase.js';
import { previewPurchase } from '../lib/callables.js';

const THEMES = {
    charcoal: {
        box: 'border border-charcoal-accent/20 bg-charcoal-pageDark/50 p-4',
        title: 'font-display text-charcoal-accent text-xs mb-2 font-semibold',
        input: 'flex-grow min-w-0 bg-charcoal-pageDark border border-charcoal-accent/30 px-3 py-2 text-charcoal-text font-serif text-sm focus:outline-none focus:border-charcoal-accent font-medium rounded-full',
        btn: 'px-4 py-2 border border-charcoal-accent/50 text-charcoal-accent font-display text-sm hover:bg-charcoal-accent/10 disabled:opacity-40 disabled:cursor-not-allowed font-semibold',
        applied: 'font-serif text-charcoal-text text-sm',
        appliedCode: 'font-display text-charcoal-accent font-semibold text-xs',
        remove: 'font-serif italic text-charcoal-muted text-xs hover:text-charcoal-accent transition-colors underline',
        error: 'font-serif text-red-300 text-xs mt-2'
    },
    burgundy: {
        box: 'border border-burgundy-accent/30 bg-burgundy-page/60 p-4',
        title: 'font-display text-burgundy-accent text-xs mb-2 font-semibold',
        input: 'flex-grow min-w-0 bg-burgundy-pageDark border border-burgundy-accent/30 px-3 py-2 text-burgundy-text font-serif text-sm focus:outline-none focus:border-burgundy-accent font-medium rounded-full',
        btn: 'px-4 py-2 border border-burgundy-accent/50 text-burgundy-accent font-display text-sm hover:bg-burgundy-accent/10 disabled:opacity-40 disabled:cursor-not-allowed font-semibold',
        applied: 'font-serif text-burgundy-text text-sm',
        appliedCode: 'font-display text-burgundy-accent font-semibold text-xs',
        remove: 'font-serif italic text-burgundy-muted text-xs hover:text-burgundy-accent transition-colors underline',
        error: 'font-serif text-red-300 text-xs mt-2'
    }
};

const REASON_COPY = {
    invalid_promo: "That code isn't valid or has been turned off.",
    promo_not_applicable: "That code doesn't apply to anything in this purchase."
};

// Format a minor-unit amount for display (HK$800 / HK$84.15 style).
export const fmtMinorHKD = (minor) => 'HK$' + (minor / 100).toFixed(2).replace(/\.00$/, '');

/**
 * @param packIds     packs in the purchase (the preview is priced on these)
 * @param applied     { code, preview } | null -- parent-owned applied state
 * @param onApplied   ({ code, preview }) => void
 * @param onRemoved   () => void
 * @param initialCode optional code to auto-apply once on mount (PayMe handoff)
 */
export default function PromoCodeBox({ theme = 'charcoal', packIds, applied, onApplied, onRemoved, initialCode }) {
    const t = THEMES[theme] || THEMES.charcoal;
    const [value, setValue] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const autoApplied = useRef(false);

    // opts.auto: a handoff auto-apply -- failures stay SILENT (the student never
    // typed anything on this page, so an unprompted red error would be noise).
    const apply = async (raw, opts = {}) => {
        const code = (raw || '').trim().toUpperCase();
        if (!code || busy) return;
        if (AUTH_MODE === 'mock') {
            if (!opts.auto) setError('Promo codes need emulator or firebase mode.');
            return;
        }
        setBusy(true); setError(null);
        try {
            const res = await previewPurchase({ packIds, promoCode: code });
            if (res && res.ok) {
                onApplied({ code: res.appliedCode, preview: res });
                setValue('');
            } else if (!opts.auto) {
                setError(REASON_COPY[res && res.reason] || REASON_COPY.invalid_promo);
            }
        } catch (e) {
            if (!opts.auto) setError((e && e.message) || 'Could not check that code. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    // One-shot auto-apply for the checkout -> PayMe handoff (never in mock mode).
    useEffect(() => {
        if (initialCode && !applied && !autoApplied.current && AUTH_MODE !== 'mock') {
            autoApplied.current = true;
            apply(initialCode, { auto: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCode]);

    return (
        <div className={t.box}>
            <div className={t.title}>Promo code</div>
            {applied ? (
                <div className="flex flex-wrap items-center gap-3">
                    <span className={t.applied}>
                        <span className={t.appliedCode}>{applied.code}</span>
                        {' '}applied — {applied.preview.discountPercent}% off,
                        you save {fmtMinorHKD(applied.preview.discountTotal)}.
                    </span>
                    <button type="button" className={t.remove} onClick={onRemoved}>Remove</button>
                </div>
            ) : (
                <div className="flex gap-2">
                    <input value={value} onChange={(e) => { setValue(e.target.value.toUpperCase()); setError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(value); } }}
                        placeholder="e.g. LAUNCH20" maxLength={32} className={t.input} />
                    <button type="button" className={t.btn} onClick={() => apply(value)} disabled={busy || !value.trim()}>
                        {busy ? 'Checking…' : 'Apply'}
                    </button>
                </div>
            )}
            {error && <p className={t.error}>{error}</p>}
        </div>
    );
}
